import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { submitInquiry } from "@/lib/api";

export default function ContactForm() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "General enquiry", message: "" });
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please fill name, email, phone and message.");
            return;
        }
        setLoading(true);
        try {
            await submitInquiry({ inquiry_type: "contact", ...form });
            toast.success("Message sent. Our team will reach out shortly.");
            setForm({ name: "", email: "", phone: "", company: "", subject: "General enquiry", message: "" });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm"
        >
            <h3 className="font-display text-xl sm:text-2xl font-bold text-navy">Send us a message</h3>
            <p className="text-sm text-slate-500 mt-1">We&apos;ll respond within one working hour.</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Field label="Full name" required>
                    <input value={form.name} onChange={update("name")} data-testid="contact-input-name" className={inputCls} placeholder="John Doe" />
                </Field>
                <Field label="Email" required>
                    <input type="email" value={form.email} onChange={update("email")} data-testid="contact-input-email" className={inputCls} placeholder="you@company.com" />
                </Field>
                <Field label="Phone" required>
                    <input value={form.phone} onChange={update("phone")} data-testid="contact-input-phone" className={inputCls} placeholder="+91 98xxxxxxxx" />
                </Field>
                <Field label="Company">
                    <input value={form.company} onChange={update("company")} data-testid="contact-input-company" className={inputCls} placeholder="Company name" />
                </Field>
                <div className="sm:col-span-2">
                    <Field label="Subject">
                        <input value={form.subject} onChange={update("subject")} data-testid="contact-input-subject" className={inputCls} />
                    </Field>
                </div>
                <div className="sm:col-span-2">
                    <Field label="Message" required>
                        <textarea rows={5} value={form.message} onChange={update("message")} data-testid="contact-input-message" className={`${inputCls} resize-none`} placeholder="Tell us about your project…" />
                    </Field>
                </div>
            </div>

            <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition-colors shadow-md"
            >
                <Send className="w-4 h-4" /> {loading ? "Sending…" : "Send Message"}
            </button>
        </form>
    );
}

const inputCls =
    "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-colors";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                {label} {required && <span className="text-brand">*</span>}
            </span>
            {children}
        </label>
    );
}
